import { useState, useEffect } from "react";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import {
  Building2, Users, TrendingUp, DollarSign, BarChart3, FileText, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { Pagination, paginate } from "@/components/Pagination";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: ${(p.value || 0).toLocaleString()}</p>
      ))}
    </div>
  );
};

function formatCurrency(val) {
  if (!val && val !== 0) return "$0";
  if (Math.abs(val) >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (Math.abs(val) >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toLocaleString()}`;
}

export default function GovernancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await API.get("/governance/fund-overview");
        setData(res);
      } catch {
        toast.error("Failed to load fund overview");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { funds = [], totals = {} } = data || {};

  const { paginatedItems: pagedGovFunds, totalPages: govTotalPages, safePage: govSafePage } = paginate(funds, page, pageSize);

  const chartData = funds.filter(f => f.est_aum > 0).map(f => ({
    name: f.name.length > 18 ? f.name.slice(0, 16) + "..." : f.name,
    aum: f.est_aum,
  }));

  return (
    <div className="animate-fade-in" data-testid="governance-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" data-testid="governance-title">Fund Oversight</h1>
        <p className="text-muted-foreground text-xs sm:text-sm mt-1">Cross-fund analytics and governance</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {[
          { label: "Total AUM", value: formatCurrency(totals.total_aum), icon: DollarSign, color: "text-emerald-500 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
          { label: "Active Funds", value: totals.total_funds, icon: Building2, color: "text-primary", bg: "bg-primary/10 border-primary/20" },
          { label: "Total Investors", value: totals.total_investors, icon: Users, color: "text-cyan-500 dark:text-cyan-400", bg: "bg-cyan-500/10 border-cyan-500/20" },
          { label: "Total Revenue", value: formatCurrency(totals.total_fees), icon: TrendingUp, color: "text-amber-500 dark:text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
        ].map((s) => (
          <Card key={s.label} className={s.bg} data-testid={`governance-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-3 sm:p-4 flex items-center gap-2.5 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-background/50 flex items-center justify-center flex-shrink-0">
                <s.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${s.color}`} strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-lg sm:text-2xl font-bold font-heading truncate">{s.value}</p>
                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* AUM Chart */}
      {chartData.length > 0 && (
        <Card className="mb-6" data-testid="aum-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading">AUM by Fund</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 12, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(217 33% 17%)" />
                  <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} tickFormatter={(v) => formatCurrency(v)} width={54} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="aum" name="AUM" radius={[4, 4, 0, 0]}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={["#2563EB", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899"][i % 5]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fund Comparison Table */}
      <Card data-testid="fund-comparison-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading">Fund Comparison</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {funds.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">No funds found</div>
          ) : (
            <>
            {/* Mobile stacked list (< md) — same data, no horizontal scroll */}
            <div className="md:hidden divide-y divide-border" data-testid="fund-comparison-mobile">
              {pagedGovFunds.map((f) => (
                <div key={f.id} className="p-4 space-y-2.5" data-testid={`fund-card-${f.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{f.name}</p>
                      <p className="text-xs text-muted-foreground">{f.reporting_frequency || "N/A"}</p>
                    </div>
                    <Badge
                      variant="outline"
                      className={
                        f.status === "active"
                          ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px] flex-shrink-0"
                          : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30 text-[10px] flex-shrink-0"
                      }
                    >
                      {f.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Investors</span><span className="truncate text-right">{f.investor_count}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Est. AUM</span><span className="font-semibold truncate text-right">{formatCurrency(f.est_aum)}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Subscriptions</span><span className="text-emerald-400 truncate text-right flex items-center justify-end gap-0.5"><ArrowUpRight className="w-3 h-3" />{formatCurrency(f.total_subscriptions)}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Redemptions</span><span className="text-red-400 truncate text-right flex items-center justify-end gap-0.5"><ArrowDownRight className="w-3 h-3" />{formatCurrency(f.total_redemptions)}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Net Income</span><span className="truncate text-right">{formatCurrency(f.net_income)}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Fees</span><span className="text-amber-400 truncate text-right">{formatCurrency(f.total_fees)}</span></div>
                  </div>
                </div>
              ))}
              {/* Totals card (mobile) */}
              <div className="p-4 space-y-2.5 bg-secondary/30" data-testid="fund-comparison-mobile-totals">
                <p className="font-semibold text-sm">Total ({funds.length} funds)</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                  <div className="flex justify-between gap-2"><span className="text-muted-foreground">Investors</span><span className="truncate text-right">{totals.total_investors}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-muted-foreground">AUM</span><span className="font-semibold truncate text-right">{formatCurrency(totals.total_aum)}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-muted-foreground">Subscriptions</span><span className="text-emerald-400 truncate text-right">{formatCurrency(totals.total_subscriptions)}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-muted-foreground">Redemptions</span><span className="text-red-400 truncate text-right">{formatCurrency(totals.total_redemptions)}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-muted-foreground">Net Income</span><span className="truncate text-right">{formatCurrency(totals.total_income - totals.total_fees)}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-muted-foreground">Fees</span><span className="text-amber-400 truncate text-right">{formatCurrency(totals.total_fees)}</span></div>
                </div>
              </div>
            </div>

            {/* Desktop / tablet table (>= md) — original layout preserved */}
            <div className="hidden md:block">
            <Table data-testid="fund-comparison-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">Fund</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Investors</TableHead>
                  <TableHead className="text-right">Subscriptions</TableHead>
                  <TableHead className="text-right">Redemptions</TableHead>
                  <TableHead className="text-right">Net Income</TableHead>
                  <TableHead className="text-right">Fees</TableHead>
                  <TableHead className="text-right pr-6">Est. AUM</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedGovFunds.map((f) => (
                  <TableRow key={f.id} data-testid={`fund-row-${f.id}`}>
                    <TableCell className="pl-6">
                      <div>
                        <p className="font-medium text-sm">{f.name}</p>
                        <p className="text-xs text-muted-foreground">{f.reporting_frequency || "N/A"}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={f.status === "active" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]" : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30 text-[10px]"}>
                        {f.status}
                      </Badge>
                    </TableCell>
                    <TableCell><span className="text-sm">{f.investor_count}</span></TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm text-emerald-400 flex items-center justify-end gap-0.5">
                        <ArrowUpRight className="w-3 h-3" />{formatCurrency(f.total_subscriptions)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm text-red-400 flex items-center justify-end gap-0.5">
                        <ArrowDownRight className="w-3 h-3" />{formatCurrency(f.total_redemptions)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right"><span className="text-sm">{formatCurrency(f.net_income)}</span></TableCell>
                    <TableCell className="text-right"><span className="text-sm text-amber-400">{formatCurrency(f.total_fees)}</span></TableCell>
                    <TableCell className="text-right pr-6"><span className="text-sm font-semibold">{formatCurrency(f.est_aum)}</span></TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                <TableRow className="bg-secondary/30 font-semibold">
                  <TableCell className="pl-6">Total ({funds.length} funds)</TableCell>
                  <TableCell />
                  <TableCell>{totals.total_investors}</TableCell>
                  <TableCell className="text-right text-emerald-400">{formatCurrency(totals.total_subscriptions)}</TableCell>
                  <TableCell className="text-right text-red-400">{formatCurrency(totals.total_redemptions)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(totals.total_income - totals.total_fees)}</TableCell>
                  <TableCell className="text-right text-amber-400">{formatCurrency(totals.total_fees)}</TableCell>
                  <TableCell className="text-right pr-6">{formatCurrency(totals.total_aum)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            </div>
            </>
          )}
          {!loading && funds.length > 0 && (
            <div className="px-4 sm:px-6 pb-4">
              <Pagination currentPage={govSafePage} totalPages={govTotalPages} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
